import { CalendarCheck, Star } from '@phosphor-icons/react';
import {
  addDays,
  onceEntry,
  suggestIcon,
  weekdayOf,
  WEEKDAY_SHORT,
  type Assignees,
  type Daypart,
  type Repeat,
  type Task,
  type Todo,
} from '@shh/shared';
import {
  ChoiceGroup,
  FormSection,
  MonthPicker,
  MultiChoice,
  SegmentedControl,
  Text,
} from '@shh/ui';
import { useState } from 'react';
import { saveTask, saveTodo } from '../data/api';
import { kids, memberById } from '../data/derive';
import { useFamily } from '../data/family';
import { formsText } from '../i18n/forms';
import { locale } from '../lib/locale';
import { dayTitle, dayTitleShort, monthName, monthTitle } from './calendar/format';
import {
  Actions,
  inRange,
  More,
  personChoice,
  StarAmount,
  TitleField,
  useSave,
  WhoField,
} from './forms';

// ---------------------------------------------------------------------------------------------
type Mode = 'one' | 'shared' | 'all';
type When =
  'today' | 'daily' | 'schooldays' | 'weekend' | 'days' | 'perWeek' | 'perWeekend' | 'perMonth';

/** Which "Wie oft?" choice an existing repeat corresponds to. */
function whenOf(repeat: Repeat): When {
  if (repeat.kind === 'once') return 'today';
  if (repeat.kind === 'daily') return 'daily';
  if (repeat.kind === 'flex') {
    return repeat.per === 'week' ? 'perWeek' : repeat.per === 'weekend' ? 'perWeekend' : 'perMonth';
  }
  const d = [...repeat.days].sort().join();
  return d === '1,2,3,4,5' ? 'schooldays' : d === '6,7' ? 'weekend' : 'days';
}

export function TaskForm({
  onDone,
  preset,
  task,
}: {
  onDone: () => void;
  preset?: { memberId?: string };
  /** Edit this task instead of creating a new one. */
  task?: Task;
}) {
  const f = useFamily();
  const children = kids(f);
  // parents may have tasks too (1.9) – their stars grow the family goal; kids come first
  const people = [...children, ...f.members.filter((m) => m.role === 'parent')];
  const a = task?.assignees;
  const [title, setTitle] = useState(task?.title ?? '');
  const [mode, setMode] = useState<Mode>(
    !a ? 'one' : a === 'all' ? 'all' : a.length > 1 ? 'shared' : 'one',
  );
  const [one, setOne] = useState(
    (Array.isArray(a) && a.length === 1 ? a[0] : undefined) ??
      preset?.memberId ??
      children[0]?.id ??
      '',
  );
  const [shared, setShared] = useState<string[]>(
    Array.isArray(a) && a.length > 1 ? a : children.slice(0, 2).map((m) => m.id),
  );
  const [when, setWhen] = useState<When>(task ? whenOf(task.repeat) : 'daily');
  const [days, setDays] = useState<string[]>(
    task?.repeat.kind === 'weekly' ? task.repeat.days.map(String) : [String(weekdayOf(f.today))],
  );
  const [daypart, setDaypart] = useState<Daypart>(task?.daypart ?? 'afternoon');
  const [points, setPoints] = useState(String(task?.points ?? 5));
  const [approval, setApproval] = useState<'yes' | 'no' | 'auto'>(
    !task || task.requiresApproval === task.points >= 10
      ? 'auto'
      : task.requiresApproval
        ? 'yes'
        : 'no',
  );
  const { busy, error, save } = useSave(onDone);
  // teens plan their own jobs: just for them, without stars (1.28, the rules check it too)
  const teen = f.role === 'teen';

  const assignees: Assignees = teen
    ? [f.meId ?? '']
    : mode === 'all'
      ? 'all'
      : mode === 'one'
        ? [one]
        : shared;
  const repeat: Repeat =
    when === 'today'
      ? // editing a one-off keeps its day
        { kind: 'once', date: task?.repeat.kind === 'once' ? task.repeat.date : f.today }
      : when === 'daily'
        ? { kind: 'daily' }
        : when === 'schooldays'
          ? { kind: 'weekly', days: [1, 2, 3, 4, 5] }
          : when === 'weekend'
            ? { kind: 'weekly', days: [6, 7] }
            : when === 'perWeek'
              ? { kind: 'flex', per: 'week' }
              : when === 'perWeekend'
                ? { kind: 'flex', per: 'weekend' }
                : when === 'perMonth'
                  ? { kind: 'flex', per: 'month' }
                  : { kind: 'weekly', days: days.map(Number) };
  const requiresApproval =
    !teen && (approval === 'auto' ? Number(points) >= 10 : approval === 'yes');
  const valid =
    title.trim() &&
    inRange(points, 0, 500) &&
    (mode !== 'one' || one) &&
    (mode !== 'shared' || shared.length >= 2) &&
    (when !== 'days' || days.length);

  return (
    <form className="flex flex-col gap-gap-lg" onSubmit={(e) => e.preventDefault()}>
      <TitleField
        label={formsText.todo}
        placeholder={formsText.taskExample}
        value={title}
        onChange={setTitle}
      />
      {!teen && (
        <>
          <ChoiceGroup
            label={formsText.forWhom}
            value={mode}
            onChange={setMode}
            choices={[
              { value: 'one', label: formsText.onePerson },
              { value: 'shared', label: formsText.shared, description: formsText.sharedHint },
              { value: 'all', label: formsText.forAll, description: formsText.forAllHint },
            ]}
          />
          {mode === 'one' && (
            <ChoiceGroup
              label={formsText.who}
              layout="tile"
              value={one}
              onChange={setOne}
              choices={people.map(personChoice)}
            />
          )}
          {mode === 'shared' && (
            <MultiChoice
              label={formsText.between}
              description={formsText.betweenHint}
              layout="tile"
              value={shared}
              onChange={setShared}
              choices={people.map(personChoice)}
            />
          )}
          {(mode === 'one' ? [one] : mode === 'shared' ? shared : []).some(
            (id) => memberById(f, id)?.role === 'parent',
          ) && (
            <Text size="sm" tone="secondary">
              {formsText.parentStars}
            </Text>
          )}
        </>
      )}
      <ChoiceGroup
        label={formsText.howOften}
        value={when}
        onChange={setWhen}
        choices={[
          // a one-off is "Einmal" now (1.28)
          { value: 'daily', label: formsText.everyDay },
          { value: 'schooldays', label: formsText.monFri },
          { value: 'weekend', label: formsText.satSun },
          { value: 'days', label: formsText.someDays },
          // once within the period, any day – then it is off the list (Mike, 04.10.)
          { value: 'perWeek', label: formsText.perWeek },
          { value: 'perWeekend', label: formsText.perWeekend },
          { value: 'perMonth', label: formsText.perMonth },
        ]}
      />
      {when === 'days' && (
        <MultiChoice
          label={formsText.whichDays}
          value={days}
          onChange={setDays}
          choices={WEEKDAY_SHORT[locale].map((d, i) => ({ value: String(i + 1), label: d }))}
        />
      )}
      <ChoiceGroup
        label={formsText.daypart}
        value={daypart}
        onChange={setDaypart}
        choices={[
          { value: 'morning', label: formsText.morning },
          { value: 'afternoon', label: formsText.afternoon },
          { value: 'evening', label: formsText.evening },
          { value: 'anytime', label: formsText.anytime },
        ]}
      />
      {!teen && (
        <>
          <StarAmount
            label={formsText.stars}
            presets={['0', '2', '5', '10', '20']}
            format={(p) => (p === '0' ? formsText.noStars : '+' + p)}
            value={points}
            onChange={setPoints}
            min={0}
            max={500}
          />
          <More label={formsText.approval}>
            <ChoiceGroup
              label={formsText.approvalAsk}
              description={formsText.approvalHint}
              value={approval}
              onChange={setApproval}
              choices={[
                { value: 'auto', label: formsText.auto },
                { value: 'yes', label: formsText.yes },
                { value: 'no', label: formsText.noAtOnce },
              ]}
            />
          </More>
        </>
      )}
      <Actions
        onCancel={onDone}
        saveLabel={task ? formsText.save : formsText.addTask}
        disabled={!valid}
        busy={busy}
        error={error}
        onSave={() =>
          save(() =>
            saveTask(f.householdId, {
              id: task?.id,
              title: title.trim(),
              // a chosen icon stays unless the task got a new name
              icon: task && title.trim() === task.title ? task.icon : suggestIcon(title),
              points: teen ? 0 : Number(points),
              daypart,
              assignees,
              repeat,
              requiresApproval,
              createdBy: task?.createdBy ?? f.meId,
            }),
          )
        }
      />
    </form>
  );
}

// ---------------------------------------------------------------------------------------------
/**
 * "Einmal" (1.28): one job, maybe with a day and stars. Without stars it is stored as a to-do,
 * with stars as a one-off task (`onceEntry`); editing keeps what it is.
 */
function OnceForm({
  onDone,
  todo,
  task,
}: {
  onDone: () => void;
  todo?: Todo;
  /** a one-off task (`repeat.kind === 'once'`) */
  task?: Task;
}) {
  const f = useFamily();
  const dueOf = todo ? todo.due : task?.repeat.kind === 'once' ? task.repeat.date : undefined;
  const [title, setTitle] = useState(todo?.title ?? task?.title ?? '');
  const [who, setWho] = useState<Assignees>(
    todo?.assignees ?? task?.assignees ?? (f.meId ? [f.meId] : 'all'),
  );
  const quick = (d?: string) =>
    !d ? 'none' : [f.today, addDays(f.today, 1)].includes(d) ? d : 'date';
  const [due, setDue] = useState<string>(quick(dueOf));
  // a concrete day (Mike, 20.10.: "konkrete Datumsauswahl als Fälligkeit")
  const [day, setDay] = useState(dueOf ?? addDays(f.today, 1));
  const [points, setPoints] = useState(String(task?.points ?? 0));
  const { busy, error, save } = useSave(onDone);
  const dueDate = due === 'none' ? undefined : due === 'date' ? day : due;
  // the same lines as the event form (1.25): "Bis wann" folded, a quick job is title + save
  const [open, setOpen] = useState(false);
  const [starsOpen, setStarsOpen] = useState(false);
  // stars are for parents to give; teens plan their own jobs without (concept 24.10.), and a
  // to-do being edited stays a to-do (stars would need a new document)
  const withStars = f.role !== 'teen' && !todo;
  const valid =
    title.trim() && inRange(points, 0, 500) && !(due === 'date' && day < f.today && !dueOf);
  return (
    <form className="flex flex-col gap-gap-md" onSubmit={(e) => e.preventDefault()}>
      <TitleField
        label={formsText.todo}
        placeholder={formsText.todoExample}
        value={title}
        onChange={setTitle}
      />
      <WhoField
        label={formsText.whoCares}
        value={who}
        onChange={setWho}
        people={f.members}
        layout="person"
      />
      <FormSection
        icon={CalendarCheck}
        label={formsText.byWhen}
        summary={dueDate ? dayTitleShort(dueDate) : formsText.sometime}
        isOpen={open}
        onOpenChange={setOpen}
      >
        <ChoiceGroup
          label={formsText.byWhen}
          value={due}
          onChange={(v) => {
            setDue(v);
            if (v !== 'date') setOpen(false);
          }}
          choices={[
            { value: 'none', label: formsText.sometime },
            { value: f.today, label: formsText.today },
            { value: addDays(f.today, 1), label: formsText.tomorrow },
            { value: addDays(f.today, 7 - weekdayOf(f.today)), label: formsText.thisWeek },
            { value: 'date', label: formsText.otherDay },
          ]}
        />
        {due === 'date' && (
          <MonthPicker
            value={day}
            today={f.today}
            weekdays={[...WEEKDAY_SHORT[locale]]}
            monthLabel={monthTitle}
            monthName={monthName}
            dayLabel={dayTitle}
            prevLabel={formsText.prevMonth}
            nextLabel={formsText.nextMonth}
            pickMonthLabel={formsText.pickMonth}
            onPick={(d) => {
              setDay(d);
              setOpen(false);
            }}
          />
        )}
      </FormSection>
      {withStars && (
        <FormSection
          icon={Star}
          label={formsText.stars}
          summary={points === '0' ? formsText.noStars : '+' + points}
          isOpen={starsOpen}
          onOpenChange={setStarsOpen}
        >
          <StarAmount
            label={formsText.stars}
            presets={['0', '2', '5', '10']}
            format={(p) => (p === '0' ? formsText.noStars : '+' + p)}
            value={points}
            onChange={setPoints}
            min={0}
            max={500}
          />
        </FormSection>
      )}
      <Actions
        onCancel={onDone}
        saveLabel={todo || task ? formsText.save : formsText.addTask}
        disabled={!valid}
        busy={busy}
        error={error}
        onSave={() =>
          save(() => {
            const old = todo ?? task;
            const entry = onceEntry(
              {
                title: title.trim(),
                // a chosen icon stays unless the job got a new name
                icon: old && title.trim() === old.title ? old.icon : suggestIcon(title),
                assignees: who,
                due: dueDate,
                points: withStars ? Number(points) : 0,
                createdBy: old?.createdBy ?? f.meId,
              },
              f.today,
              todo ? 'todo' : task ? 'task' : undefined,
            );
            return entry.kind === 'todo'
              ? saveTodo(f.householdId, { ...entry.todo, id: todo?.id })
              : saveTask(f.householdId, { ...entry.task, id: task?.id });
          })
        }
      />
    </form>
  );
}

/**
 * One form for jobs (1.28, concept 24.10.): "Einmal · Regelmäßig" on top. Editing shows only
 * the part the job already is – the data stays split (to-do / task).
 */
export function JobForm({
  onDone,
  preset,
  task,
  todo,
}: {
  onDone: () => void;
  preset?: { memberId?: string; often?: Often };
  task?: Task;
  todo?: Todo;
}) {
  const once = !!todo || task?.repeat.kind === 'once';
  const [often, setOften] = useState<Often>(
    task || todo ? (once ? 'once' : 'regular') : (preset?.often ?? 'once'),
  );
  return (
    <div className="flex flex-col gap-gap-lg">
      {!task && !todo && (
        <SegmentedControl
          label={formsText.oftenAsk}
          value={often}
          onChange={setOften}
          options={[
            { value: 'once', label: formsText.onceJob },
            { value: 'regular', label: formsText.regularJob },
          ]}
          className="self-start max-sm:self-stretch"
        />
      )}
      {often === 'once' ? (
        <OnceForm onDone={onDone} todo={todo} task={once ? task : undefined} />
      ) : (
        <TaskForm onDone={onDone} preset={preset} task={task} />
      )}
    </div>
  );
}

export type Often = 'once' | 'regular';
